/*
 * THE SERVER'S SIDE OF REPLAY CLIP LINKS, as the app's screens see it (the same ShareApi the demo's browser store implements). Docs: docs/SHARING_SECURITY.md.
 *   create / list / renew / revoke go through the app's own authenticated calls; the OWNER is the session, never anything sent from here.
 *   openSharedSnapshot is what a RECIPIENT's browser does: it has no account, so it sends nothing but the token (in the BODY of a POST, never in an address a log or a Referer would keep, and with no cookie).
 */
import config from "../config/config";
import { eventEmitter } from "../eventEmitters/EventEmitters";
import { session } from "../services/session";
import * as http from "../util/http";
import storage from "../util/storage";
import { clipForServer, ShareApi, ShareLimits, ShareRefused, snapshotFromServer } from "./shareStore";
import { ShareSnapshot } from "./shareSnapshot";
import { forgetToken, recallToken, rememberToken } from "./shareTokenMemory";

const DAY_MS = 86_400_000;
let lastLimits: ShareLimits | null = null;

const rpc = async (method: string, params: Record<string, unknown>): Promise<any> => {
  const reply = await session({ id: 0, jsonprc: "2.0", method, params });

  return reply?.data?.result ?? null;
};

/** a link as the list shows it: what the server says of it (never a token), with the secret the browser remembers, if it does */
const fromSummary = (s: any): ShareSnapshot => ({
  version: 1,
  token: recallToken(String(s.id)),
  id: String(s.id),
  views: Number(s.views) || 0,
  label: String(s.label || ""),
  siteName: String(s.siteName || ""),
  createdAt: Number(s.createdAt),
  expiresAt: Number(s.expiresAt),
  ...(s.revokedAt ? { revokedAt: Number(s.revokedAt) } : {}),
  startMs: Number(s.startMs),
  endMs: Number(s.endMs),
  actions: [],
  sampled: false,
  pages: [],
  goalUrls: [],
  notes: [],
  options: { layout: "cluster", flowLines: false, flowStyle: "lines", icons: true, hideUnknown: false, labels: false, order: "desc", allowViewChange: true },
});

const refresh = async (): Promise<{ shares: ShareSnapshot[]; limits: ShareLimits | null }> => {
  const answer = await rpc("listPlaybackShares", {});
  // a list that could not be read is NOT an empty list (the server restarting, a dropped connection): the screens say so and offer to try again
  if (!answer || answer.ok !== true) throw new Error("could not read the replay links");
  lastLimits = { maxLive: Number(answer.limits?.maxLive) || 1, maxExpiryDays: Number(answer.limits?.maxExpiryDays) || 7, live: Number(answer.live) || 0 };

  return { shares: (answer.shares as any[]).map(fromSummary), limits: lastLimits };
};

export const serverShareApi: ShareApi = {
  // the same as the server's own cap (MAX_CLIP_BYTES): a clip over it is refused there, so the dialog never offers to make one
  maxBytes: 3_000_000,
  create: async (snapshot) => {
    const days = Math.max(1, Math.round((snapshot.expiresAt - snapshot.createdAt) / DAY_MS));
    const reply = await http.post("/share/create", { params: { siteId: storage.get("siteId"), clip: clipForServer(snapshot), expiresInDays: days } });
    const body = reply?.data;
    if (!body || body.ok !== true || typeof body.token !== "string") {
      const refusal = reply?.error || body || {};
      throw new ShareRefused(String(refusal.reason || "error"), refusal.where, refusal.limits);
    }
    rememberToken(String(body.id), body.token);
    if (body.limits) lastLimits = { maxLive: body.limits.maxLive, maxExpiryDays: body.limits.maxExpiryDays, live: (lastLimits?.live || 0) + 1 };
    eventEmitter.emit("sharesChanged");

    return { token: body.token, expiresAt: Number(body.expiresAt) };
  },
  // a recipient's open does not go through here (openSharedSnapshot); an owner's own preview is a browser draft (localShareApi)
  get: async () => null,
  list: async () => (await refresh()).shares,
  revoke: async (key) => {
    await rpc("revokePlaybackShare", { id: key });
    forgetToken(key);
    eventEmitter.emit("sharesChanged");
  },
  renew: async (key, days) => {
    await rpc("renewPlaybackShare", { id: key, days });
    eventEmitter.emit("sharesChanged");
  },
  limits: async () => { try { return (await refresh()).limits; } catch { return lastLimits; } },
};

/** the server refuses `<` and `>` in any clip text; the viewer checks again, because tooltips draw some of this text as HTML and a clip is somebody else's data (defence in depth, docs/SHARING_SECURITY.md) */
export const clipHasMarkup = (clip: any): boolean => /[<>]/.test(JSON.stringify({ ...clip, packed: undefined })) || /[<>]/.test(JSON.stringify(clip?.packed?.dict ?? []));

export type OpenOutcome =
  | { kind: "live"; snapshot: ShareSnapshot }
  | { kind: "expired" | "revoked" | "missing" | "busy" | "error" };

/**
 * What a recipient's browser does with a token. No cookie, no authorisation header, no referrer, no cache: a plain request that says only the token. 200 live (the clip), 410 ended (and nothing more), 404 nothing,
 * 429 too many tries from this address, anything else an error. What comes back is somebody else's data and is never trusted: the viewer draws it as text.
 */
export const openSharedSnapshot = async (token: string): Promise<OpenOutcome> => {
  try {
    const base = String(config.baseUrl || "").replace(/\/+$/, "");
    const response = await fetch(`${base}/share/open`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
      credentials: "omit",
      cache: "no-store",
      referrerPolicy: "no-referrer",
    });
    if (response.status === 404) return { kind: "missing" };
    if (response.status === 429) return { kind: "busy" };
    const body: any = await response.json().catch(() => null);
    if (response.status === 410) return { kind: body?.state === "revoked" ? "revoked" : "expired" };
    if (response.status !== 200 || !body || body.state !== "live" || !body.clip) return { kind: "error" };

    if (clipHasMarkup(body.clip)) return { kind: "error" };

    return { kind: "live", snapshot: snapshotFromServer(body.clip, { token, createdAt: Number(body.createdAt) || Date.now(), expiresAt: Number(body.expiresAt) || Date.now() }) };
  } catch {
    return { kind: "error" };
  }
};
